import { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { ArrowRight, CalendarDays, Users, Waves, Mail, MapPin, Facebook, Phone, ChevronDown } from 'lucide-react'
import { Link } from 'react-router-dom'
import { companyInfo, designTokens } from '../data/siteData'
import { useSiteContent } from '../context/SiteContentContext'
import { CardGrid, ContactInfoStrip, HeroBanner, SectionHeading, SplitFeature, RichText } from '../components/ContentBlocks'
import { ContactForm } from '../components/ContactForm'

function pickClasses(classes, keywords) {
  if (!keywords.length) return classes.slice(0, 6)
  return classes.filter((item) => keywords.includes((item.category || '').toLowerCase())).slice(0, 6)
}

function getBadgeStyles(badgeStyle, index) {
  if (badgeStyle === 'lime') {
    return {
      iconBox: 'bg-lime-100 text-lime-800',
      badge: 'bg-lime-100 text-lime-800',
      activeChevron: 'text-lime-800',
    }
  }
  if (badgeStyle === 'stone' || badgeStyle === 'gray' || badgeStyle === 'grey') {
    return {
      iconBox: 'bg-stone-200 text-slate-700',
      badge: 'bg-stone-200 text-slate-700',
      activeChevron: 'text-slate-700',
    }
  }
  if (badgeStyle === 'blue') {
    return {
      iconBox: 'bg-[#3B70E5]/10 text-[#3B70E5]',
      badge: 'bg-[#3B70E5]/10 text-[#3B70E5]',
      activeChevron: 'text-[#3B70E5]',
    }
  }
  if (index === 2) {
    return {
      iconBox: 'bg-stone-200 text-slate-700',
      badge: 'bg-stone-200 text-slate-700',
      activeChevron: 'text-slate-700',
    }
  }
  if (index === 3) {
    return {
      iconBox: 'bg-lime-100 text-lime-800',
      badge: 'bg-lime-100 text-lime-800',
      activeChevron: 'text-lime-800',
    }
  }
  return {
    iconBox: 'bg-[#3B70E5]/10 text-[#3B70E5]',
    badge: 'bg-[#3B70E5]/10 text-[#3B70E5]',
    activeChevron: 'text-[#3B70E5]',
  }
}

const defaultCalendarEvents = [
  {
    id: 'default-1',
    date_badge: 'D. 13-15 marts 2026',
    title: 'DGI områdeopvisning i Sportium Varde',
    content: '<p>Programmet for områdeopvisning er online – og kan findes på DGIs hjemmeside eller i App’en “DGI Gymnastik”</p>',
    badge_style: 'blue',
  },
  {
    id: 'default-2',
    date_badge: 'LØRDAG d. 28 marts 2026',
    title: 'Lokalopvisning i Sportium Varde',
    content: `<p className="font-bold text-slate-900">Program lørdag d. 28. marts</p>
      <ul className="space-y-1 text-xs text-slate-600 border-l-2 border-slate-200 pl-3">
        <li><strong>11.00</strong> Faneindmarch & velkomst</li>
        <li><strong>11.15</strong> Forældre/barn</li>
        <li><strong>11.25</strong> Tumleholdet ”Kun for drenge” (3-5 år)</li>
        <li><strong>11.35</strong> MGP Dance Sportium</li>
        <li><strong>11.45</strong> Familieholdet</li>
        <li><strong>11.55</strong> Puslinge Lykkesgård og Jacobi (3-5 år)</li>
        <li><strong>12.05</strong> MGP Dance Lykkesgård</li>
        <li><strong>12.15</strong> Minispring (Sidste år i Bhv.-0. kl.)</li>
        <li><strong>12.35</strong> MGP (1.-2. kl.)</li>
        <li><strong>12.45</strong> Spring Drenge (3.-5. kl.)</li>
        <li><strong>13.00</strong> Jump</li>
        <li><strong>13.15</strong> (VG serie) og Pokaloverrækkelse</li>
        <li><strong>13.20</strong> Teen dance (5. – 7. kl.)</li>
        <li><strong>13.30</strong> Spring (1. – 2. kl.)</li>
        <li><strong>13.50</strong> Spring (6.-9. kl.)</li>
        <li><strong>14.00</strong> VIP & Tistrup Motionsmænd</li>
        <li><strong>14.20</strong> Spring Piger (3.-5. kl.)</li>
        <li><strong>14.40</strong> Rytme 15+</li>
        <li><strong>14.50</strong> Rytmedamer</li>
        <li><strong>15.00</strong> JTH</li>
        <li><strong>15.30</strong> Balle Efterskole</li>
        <li><strong>16.00</strong> Faneudmarch / farvel og tak for i år</li>
      </ul>
      <p className="text-xs text-slate-500">Vær obs på at datoen er rykket en uge i forhold til tidligere år.</p>
      <p className="text-xs text-slate-500">Programmerne, der deles ud på holdene gælder som adgangsbillet for de aktive gymnaster der skal på gulvet og som selvfølgelig har gratis adgang.</p>
      <p className="text-xs text-slate-500">Ellers er priserne; voksen 50 kr. barn 4-12 år 10 kr. børn u.3 år gratis adgang.</p>`,
    badge_style: 'blue',
  },
  {
    id: 'default-3',
    date_badge: 'Klubdag',
    title: 'Klubdag svømmerne',
    content: '<p>Vi er i fuld gang med at gentænke klubdagen som vi kender den – vi vender tilbage med et nyt tiltag.</p>',
    badge_style: 'stone',
  },
  {
    id: 'default-4',
    date_badge: 'Medio august 2026',
    title: 'Åbner vi for tilmelding på alle hold til den nye sæson',
    content: '<p>Du er naturligvis altid velkommen til at prøve holdene inden du tilmelder. På de hold, hvor vi har max deltagerantal kan vi dog ikke garantere en plads efter prøvetræningerne.</p>',
    badge_style: 'lime',
  },
]

export function HomePage() {
  const { latestNews, featuredClasses, getImage, calendarEvents } = useSiteContent()
  const heroNews = latestNews.slice(0, 3)
  const [openIndex, setOpenIndex] = useState(null)
  const displayCalendarEvents = calendarEvents && calendarEvents.length > 0 ? calendarEvents : defaultCalendarEvents

  const toggleAccordion = (index) => {
    setOpenIndex(openIndex === index ? null : index)
  }

  const featuredCards = [
    {
      title: 'Gymnastik 26/27',
      href: '/gymnastik-26-27',
      kicker: 'Hold og aktiviteter',
      text: 'Se de mange hold for børn, unge, voksne og seniorer på én samlet oversigt.',
      image: designTokens.programImage,
    },
    {
      title: 'Svømning 26/27',
      href: '/svomning-26-27',
      kicker: 'Vand og bevægelse',
      text: 'Hold i vandet med tryghed, bevægelse og plads til både begyndere og øvede.',
      image: designTokens.splitImage,
    },
    {
      title: 'Tilmelding',
      href: '/tilmelding',
      kicker: 'Sæsonstart i august',
      text: 'Få styr på sæsonstarten og find det hold, der passer til dig eller dit barn.',
      image: designTokens.homeSupportImage,
    },
  ]

  const infoCards = [
    { title: 'Sæsonstart i august', text: 'Vi åbner for den nye sæson i august og samler hold, fællesskab og praktisk info ét sted.', icon: <CalendarDays className="h-5 w-5" /> },
    { title: 'Fællesskab og bevægelse', text: 'Foreningen favner både gymnastik, dans, spring, svømning og aktiviteter for alle aldre.', icon: <Users className="h-5 w-5" /> },
    { title: 'Kontakt på tværs af kanaler', text: 'Telefon, email og Facebook er tydeligt tilgængeligt, så det er let at komme i kontakt.', icon: <Mail className="h-5 w-5" /> },
  ]

  const pageImage = getImage(latestNews[2]) || designTokens.heroImage

  return (
    <div>
      <Helmet>
        <title>Varde Gymnastikforening</title>
        <meta name="description" content="" />
        <meta property="og:title" content="Varde Gymnastikforening" />
        <meta property="og:description" content="" />
        <meta property="og:image" content="" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Varde Gymnastikforening" />
        <meta name="twitter:description" content="" />
        <meta name="twitter:image" content="" />
      </Helmet>
      <HeroBanner
        eyebrow="Varde Gymnastikforening"
        title="Varde Gymnastikforening"
        subtitle="Moderne dansk foreningsliv med hold, fællesskab, bevægelse og en tydelig sæsonstart i august."
        image={pageImage}
        ctas={[
          { label: 'Se holdene', href: '/gymnastik-26-27', primary: true },
          { label: 'Kontakt os', href: '/kontakt' },
        ]}
      />

      <section className="bg-white py-10">
        <div className="mx-auto grid max-w-7xl gap-4 px-4 sm:px-6 lg:grid-cols-3 lg:px-8">
          {infoCards.map((item) => (
            <div key={item.title} className="rounded-[1.75rem] border border-slate-200 bg-stone-50 p-6 shadow-sm">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#3B70E5]/10 text-[#3B70E5]">{item.icon}</div>
              <h2 className="mt-4 text-lg font-bold text-slate-950">{item.title}</h2>
              <p className="mt-2 text-sm leading-6 text-slate-600">{item.text}</p>
            </div>
          ))}
        </div>
      </section>

      <CardGrid
        title="Hurtig adgang"
        description="Direkte genveje til de vigtigste sider, så medlemmer og forældre hurtigt finder det relevante indhold."
        items={featuredCards}
      />

      <SplitFeature
        title="Hold og aktiviteter med plads til hele foreningen"
        description="Fra børnehold og dans til vandaktiviteter, voksne og seniorer. Vi samler det hele i en overskuelig struktur med tydelige CTA’er og praktisk overblik."
        image={designTokens.splitImage}
        cta={{ label: 'Se programmet', href: '/gymnastik-26-27' }}
      />

      {/* Vigtige datoer til kalenderen */}
      <section className="bg-white py-16 sm:py-24 border-t border-slate-100">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-start">
            {/* Content Side */}
            <div className="space-y-8">
              <div>
                <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#3B70E5]">
                  Kalender
                </span>
                <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl">
                  Vigtige datoer til kalenderen
                </h2>
                <p className="mt-4 text-lg leading-8 text-slate-600">
                  Her kommer forårets vigtigste datoer til kalenderen! <br />
                  Husk påmindelse på mobilen, så I ikke går glip af dagene!
                </p>
              </div>

              <div className="space-y-4">
                {displayCalendarEvents.map((item, index) => {
                  const style = getBadgeStyles(item.badge_style, index)
                  const isOpen = openIndex === index
                  return (
                    <div
                      key={item.id || index}
                      className="rounded-2xl border border-slate-200 bg-stone-50 overflow-hidden transition-all duration-200"
                    >
                      <button
                        onClick={() => toggleAccordion(index)}
                        className="flex w-full items-center justify-between gap-4 p-5 text-left transition hover:bg-stone-100/50 focus:outline-none"
                      >
                        <div className="flex gap-4 items-center">
                          <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${style.iconBox}`}>
                            <CalendarDays className="h-6 w-6" />
                          </div>
                          <div>
                            <span className={`inline-block rounded-md px-2.5 py-0.5 text-xs font-semibold ${style.badge}`}>
                              {item.date_badge || item.date || ''}
                            </span>
                            <h4 className="mt-1.5 font-bold text-slate-950">{item.title}</h4>
                          </div>
                        </div>
                        <ChevronDown
                          className={`h-5 w-5 text-slate-400 transition-transform duration-200 shrink-0 ${
                            isOpen ? `rotate-180 ${style.activeChevron}` : ''
                          }`}
                        />
                      </button>
                      <div
                        className={`transition-all duration-300 ease-in-out ${
                          isOpen
                            ? 'max-h-[1400px] overflow-y-auto opacity-100 border-t border-slate-200/60'
                            : 'max-h-0 opacity-0 pointer-events-none'
                        }`}
                      >
                        <div className="p-5 text-sm leading-relaxed text-slate-600 bg-white">
                          {typeof item.content === 'string' ? (
                            <RichText html={item.content} />
                          ) : (
                            item.content
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Image Side */}
            <div className="relative overflow-hidden rounded-[2rem] border border-slate-200 bg-stone-100 shadow-lg lg:aspect-square">
              <img
                src="https://assets.auxodev.dk/assets/b6209d66-5898-40d7-bfd5-6255faf6b7d0/70332d62-b86e-4249-85b4-37f0b773a477.jpg"
                alt="Vigtige datoer til kalenderen"
                className="h-full w-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-stone-100 py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Nyheder"
            title="Seneste nyt fra Varde Gymnastikforening"
            description="Få de vigtigste beskeder, arrangementer og sæsonnyheder samlet på forsiden."
          />
          <div className="mt-10 grid gap-6 lg:grid-cols-3">
            {heroNews.map((item) => (
              <Link key={item.news_slug} to={`/${item.news_slug}`} className="group overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-xl">
                <div className="p-6">
                  <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#3B70E5]">{item.date || 'Nyhed'}</p>
                  <h3 className="mt-3 text-xl font-bold tracking-tight text-slate-950">{item.title}</h3>
                  <p className="mt-3 text-sm leading-6 text-slate-600">{item.meta_description || ''}</p>
                  <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-slate-900 transition group-hover:text-[#3B70E5]">
                    Læs mere <ArrowRight className="h-4 w-4" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-8 flex justify-center">
            <Link to="/nyheder" className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800">
              Se alle nyheder
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-slate-950 py-16 text-white sm:py-24">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-[1.1fr_0.9fr] lg:px-8">
          <div>
            <SectionHeading
              eyebrow="Kontakt"
              title="Kontakt, Facebook og praktisk info"
              description="Vi gør det let at komme i kontakt og følge med i foreningslivet."
              dark
            />
            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              <a href={`mailto:${companyInfo.email}`} className="rounded-3xl border border-white/10 bg-white/5 p-5 transition hover:bg-white/10">
                <Mail className="h-5 w-5 text-[#3B70E5]" />
                <p className="mt-4 text-sm font-semibold text-white">Email</p>
                <p className="mt-2 whitespace-nowrap text-sm text-slate-300">{companyInfo.email}</p>
              </a>
              <a href={`tel:${companyInfo.phone.replace(/\s+/g, '')}`} className="rounded-3xl border border-white/10 bg-white/5 p-5 transition hover:bg-white/10">
                <Phone className="h-5 w-5 text-[#3B70E5]" />
                <p className="mt-4 text-sm font-semibold text-white">Telefon</p>
                <p className="mt-2 whitespace-nowrap text-sm text-slate-300">{companyInfo.phone}</p>
              </a>
              <a href={companyInfo.facebook} target="_blank" rel="noreferrer" className="rounded-3xl border border-white/10 bg-white/5 p-5 transition hover:bg-white/10">
                <Facebook className="h-5 w-5 text-[#3B70E5]" />
                <p className="mt-4 text-sm font-semibold text-white">Facebook</p>
                <p className="mt-2 text-sm text-slate-300">Følg med på opdateringer og nyheder</p>
              </a>
              <div className="rounded-3xl border border-white/10 bg-white/5 p-5">
                <MapPin className="h-5 w-5 text-[#3B70E5]" />
                <p className="mt-4 text-sm font-semibold text-white">Adresse</p>
                <p className="mt-2 text-sm text-slate-300">{companyInfo.address}, {companyInfo.zipCity}</p>
              </div>
            </div>
          </div>
          <div>
            <ContactForm />
          </div>
        </div>
      </section>

      <section className="bg-white py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Samlet overblik"
            title="Et aktivt og lokalt udtryk for hele Varde"
            description={(
              <>
                <h3 className="text-xl font-bold tracking-tight text-slate-950">Vi søger trænere og frivillige</h3>
                <p className="mt-4 text-base leading-7 text-slate-600">
                  Varde Gymnastikforening er en stor forening med mange hold og aktive trænere, men vi kan altid bruge flere. Har du tid og lyst til at give en hånd med på et eksisterende hold eller måske har du et nyt hold, du gerne vil starte op – så giv meget gerne lyd.
                </p>
              </>
            )}
            align="center"
          />
          <div className="mt-10 rounded-[2rem] border border-slate-200 bg-stone-50 p-6 shadow-sm sm:p-8">
            <ContactInfoStrip />
          </div>
        </div>
      </section>
    </div>
  )
}
